<template>
  <div class="point-detail">
    <div class="section-header">
      <h3>
        {{
          pointDetail.name
            ? pointDetail.name
            : $t("portfolioManager.pointDetail.sectionTitle")
        }}
      </h3>
      <div class="header-right">
        <b-button variant="primary" @click="handleReplacePortfolio"
          ><replace-icon :size="16" />{{
            $t("portfolioManager.pointDetail.replacePortfolio")
          }}</b-button
        >
        <close-icon :size="16" @click="clearPointDetail" />
      </div>
    </div>
    <div class="detail-content">
      <div class="significant-measures-container">
        <significant-measure
          v-for="(measure, i) in measures"
          :key="i"
          v-bind="measure"
          :value="pointDetail.measures[measure.key]"
          bordered
          :compareTo="
            portfolio && portfolio.portfolio_output
              ? portfolio.portfolio_output.custom_portfolio[measure.diffKey]
              : 0
          "
        />
      </div>
      <div class="charts">
        <point-detail-table :items="pointDetail.values" />
        <weights-chart :portfolio="pointDetail.values" />
        <covariance-matrix
          v-if="portfolio && portfolio.portfolio_id"
          :data="portfolio.portfolio_output.cov_matrix"
          :displayTickers="pointDetail.values"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  DARK_GREEN,
  DARK_RED,
  LIGHT_GREEN,
  LIGHT_PURPLE,
  LIGHT_RED,
} from "../utils/constants";
import { mapActions, mapGetters, mapMutations } from "vuex";
import ChartIcon from "vue-material-design-icons/ChartLineStacked.vue";
import CloseIcon from "vue-material-design-icons/Close.vue";
import CovarianceMatrix from "./charts/CovarianceMatrix.vue";
import CrossedDollar from "vue-material-design-icons/CurrencyUsdOff.vue";
import DollarIcon from "vue-material-design-icons/CurrencyUsd.vue";
import PointDetailTable from "./tables/PointDetailTable.vue";
import ReplaceIcon from "vue-material-design-icons/Replay.vue";
import RiskIcon from "vue-material-design-icons/Flash.vue";
import SignificantMeasure from "./SignificantMeasure.vue";
import WeightsChart from "./charts/WeightsChart.vue";
import { compareObjects } from "../utils/helper";

export default {
  components: {
    PointDetailTable,
    ReplaceIcon,
    CloseIcon,
    SignificantMeasure,
    CovarianceMatrix,
    WeightsChart,
  },
  name: "point-detail",
  data() {
    return {
      measures: [
        {
          title: this.$i18n.t(
            "portfolioManager.significantMeasures.expectedReturn"
          ),
          icon: DollarIcon,
          color: LIGHT_GREEN,
          darkerColor: DARK_GREEN,
          key: "expected_return",
          diffKey: "expected_return",
          percentages: true,
          unit: true,
        },
        {
          title: this.$i18n.t(
            "portfolioManager.significantMeasures.volatility"
          ),
          icon: CrossedDollar,
          color: LIGHT_RED,
          darkerColor: DARK_RED,
          key: "volatility",
          diffKey: "volatility",
          percentages: true,
          unit: true,
        },
        {
          title: this.$i18n.t("portfolioManager.sharpe"),
          icon: ChartIcon,
          color: LIGHT_PURPLE,
          darkerColor: "#7266F3",
          key: "sharpe",
          diffKey: "sharpe",
        },
        {
          title: this.$i18n.t("portfolioManager.var"),
          icon: RiskIcon,
          color: "#fae8a0",
          darkerColor: "#FFD600",
          key: "valueAtRisk_5",
          diffKey: "valueAtRisk_5",
          percentages: true,
        },
      ],
    };
  },
  computed: {
    ...mapGetters("portfolioManagerStore", [
      "pointDetail",
      "portfolio",
      "cash",
      "defaultPortfolio",
    ]),
  },
  methods: {
    ...mapMutations("portfolioManagerStore", [
      "replacePortfolio",
      "clearPointDetail",
      "clearRemovedTickers",
      "clearPointDetail",
      "setCustomPortfolio",
      "setMeasurements",
      "setEachStockResults",
    ]),
    ...mapActions("portfolioManagerStore", ["portfolioConversion"]),
    handleReplacePortfolio() {
      const eachStockResults = {};
      const tickers = this.pointDetail.values.map(({ ticker }) => ticker);
      Object.entries(
        this.defaultPortfolio.output_portfolio.each_stock_results
      ).forEach((item) => {
        const [key, value] = item;
        const filtered = Object.fromEntries(
          Object.entries(value).filter(([key]) => tickers.includes(key))
        );
        eachStockResults[key] = filtered;
      });
      this.setCustomPortfolio(this.pointDetail.values);
      this.replacePortfolio({
        coords: [
          this.pointDetail.measures.volatility,
          this.pointDetail.measures.expected_return,
        ],
      });
      this.setMeasurements(this.pointDetail.measures);
      this.setEachStockResults(eachStockResults);
      this.clearRemovedTickers();
      this.clearPointDetail();
      this.$scrollTo("#wrapper");
    },
    fillMissingData() {
      if (!this.pointDetail.values[0].capital && this.cash) {
        this.portfolioConversion({ isConversingPointDetail: true });
      }
    },
  },
  watch: {
    pointDetail(newVal, oldVal) {
      if (!compareObjects(newVal.measures, oldVal.measures)) {
        this.fillMissingData();
      }
    },
  },
  mounted() {
    this.fillMissingData();
  },
};
</script>

<style lang="scss" scoped>
.point-detail {
  margin-top: toRem(58);
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: toRem(25);
    .header-right {
      display: flex;
      .close-icon {
        cursor: pointer;
      }
      .btn {
        margin-right: 15px;
      }
    }
  }
  .significant-measures-container {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: toRem(30);
    .significant-measure {
      margin-right: toRem(50);
      min-width: 13%;
      max-width: 220px;
    }
  }
  .detail-content {
    .charts {
      display: flex;
      column-gap: 28px;
      align-items: flex-start;
      .point-detail-table {
        min-width: 400px;
      }
      .point-detail-table,
      .weights-chart-root,
      .covariance-matrix-root {
        width: calc(33% - 28px);
      }
    }
  }
  @media screen and (max-width: 1340px) {
    .significant-measures-container {
      justify-content: space-between;
      margin-bottom: toRem(25);
      .significant-measure {
        margin-right: 0;
        padding-bottom: toRem(5);
      }
    }
    .detail-content {
      .charts {
        flex-wrap: wrap;
        .point-detail-table {
          width: 100%;
        }
        .weights-chart-root,
        .covariance-matrix-root {
          margin-top: 28px;
          width: calc(50% - 14px);
        }
      }
    }
  }
}
</style>